// laborForecastApi.js – API for labor forecast and staffing recommendations
// Consumes labor-forecast table and extended shift fields (predicted_covers, required_headcount, etc.)

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp <= currentTime) return null;
      return tokens.idToken.toString();
    }
    return null;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return null;
  }
};

/**
 * Create a new labor forecast
 * POST /labor/forecast
 * @param {Object} forecastData - Forecast data
 * @param {string} forecastData.site_id - Site ID (required)
 * @param {string} forecastData.interval_start - ISO8601 timestamp (required)
 * @param {number} [forecastData.predicted_covers] - Predicted covers
 * @param {number} [forecastData.required_headcount] - Required headcount
 * @param {number} [forecastData.labor_cost_to_revenue_ratio] - Labor cost to revenue ratio
 * @returns {Promise<Object>} Created forecast object
 */
export const createForecast = async (forecastData) => {
  try {
    const token = await getAuthToken();
    if (!token) throw new Error('Authentication token not available');

    const response = await fetch(`${API_BASE_URL}/labor/forecast`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(forecastData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.endpoint = '/labor/forecast';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API error: ${response.status} for /labor/forecast`,
        error,
        {
          endpoint: '/labor/forecast',
          method: 'POST',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating forecast:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API network/fetch error for /labor/forecast`,
        error,
        {
          endpoint: '/labor/forecast',
          method: 'POST',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * List labor forecasts for a site and date
 * GET /labor/forecast?site_id={site_id}&date={YYYY-MM-DD}
 * @param {string} siteId - Site ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Array>} Array of forecast objects
 */
export const listForecasts = async (siteId, date) => {
  try {
    const token = await getAuthToken();
    if (!token) throw new Error('Authentication token not available');

    const params = new URLSearchParams({ site_id: siteId, date });
    const response = await fetch(`${API_BASE_URL}/labor/forecast?${params}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) return [];
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.endpoint = '/labor/forecast';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API error: ${response.status} for /labor/forecast`,
        error,
        {
          endpoint: '/labor/forecast',
          method: 'GET',
          status: response.status,
          siteId: siteId,
          date: date,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    // API returns { forecasts: [...] }
    if (data && Array.isArray(data.forecasts)) return data.forecasts;
    if (Array.isArray(data)) return data;
    return data.intervals || [];
  } catch (error) {
    console.error('Error listing forecasts:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API network/fetch error for /labor/forecast`,
        error,
        {
          endpoint: '/labor/forecast',
          method: 'GET',
          errorType: error.name || 'NetworkError',
          siteId: siteId,
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get a specific forecast by ID
 * GET /labor/forecast/{forecast_id}
 * @param {string} forecastId - Forecast ID
 * @returns {Promise<Object>} Forecast object
 */
export const getForecast = async (forecastId) => {
  try {
    const token = await getAuthToken();
    if (!token) throw new Error('Authentication token not available');

    const response = await fetch(`${API_BASE_URL}/labor/forecast/${forecastId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) return null;
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.endpoint = `/labor/forecast/${forecastId}`;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API error: ${response.status} for /labor/forecast/${forecastId}`,
        error,
        {
          endpoint: `/labor/forecast/${forecastId}`,
          method: 'GET',
          status: response.status,
          forecastId: forecastId,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching forecast:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_labor_forecast',
        `Labor Forecast API network/fetch error for /labor/forecast/${forecastId}`,
        error,
        {
          endpoint: `/labor/forecast/${forecastId}`,
          method: 'GET',
          errorType: error.name || 'NetworkError',
          forecastId: forecastId,
        }
      ).catch(logError => {
        console.warn('[laborForecastApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Parse API interval_start (ISO8601) to HH:mm for display.
 * @param {string} intervalStart - e.g. "2026-02-06T06:00:00.000000+00:00"
 * @returns {string} e.g. "06:00"
 */
function intervalStartToTimeLabel(intervalStart) {
  if (!intervalStart) return '00:00';
  try {
    const d = new Date(intervalStart);
    const h = d.getUTCHours();
    const m = d.getUTCMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch {
    return '00:00';
  }
}

/**
 * Transform API forecast item to heatmap interval shape.
 * API: { forecast_id, interval_start, predicted_covers, required_headcount, labor_cost_to_revenue_ratio, ... }
 * Heatmap: { forecast_id, interval_ts, interval_index, predicted_covers, required_headcount, planned_headcount, variance, ... }
 */
function forecastToInterval(apiForecast, index) {
  const required = apiForecast.required_headcount ?? 0;
  const planned = apiForecast.planned_headcount ?? required;
  const variance = apiForecast.variance ?? (planned - required);
  return {
    forecast_id: apiForecast.forecast_id,
    tenant_id: apiForecast.tenant_id,
    site_id: apiForecast.site_id,
    interval_start: apiForecast.interval_start,
    interval_ts: apiForecast.interval_ts ?? intervalStartToTimeLabel(apiForecast.interval_start),
    interval_index: apiForecast.interval_index ?? index,
    predicted_covers: apiForecast.predicted_covers,
    required_headcount: required,
    planned_headcount: planned,
    variance,
    labor_cost_to_revenue_ratio: apiForecast.labor_cost_to_revenue_ratio,
    created_at: apiForecast.created_at,
  };
}

/**
 * Get labor forecast for a site and date.
 * GET /labor/forecast?site_id={site_id}&date={YYYY-MM-DD} returns { forecasts: [...] }.
 * Transforms to heatmap interval format (interval_ts, variance, etc.).
 * @param {string} siteId - site_id
 * @param {string} date - YYYY-MM-DD
 * @param {{ roleGroup?: string, targetLaborRatio?: number }} options
 * @returns {Promise<{ intervals: Array }>}
 * 
 * example response:
 * {
  "forecasts": [
    {
      "forecast_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
      "tenant_id": "tenant-abc",
      "site_id": "site-001",
      "interval_start": "2025-02-12T09:00:00",
      "predicted_covers": 120,
      "required_headcount": 6,
      "labor_cost_to_revenue_ratio": 0.24,
      "created_at": "2025-02-11T14:30:00.123456+00:00"
    },
    {
      "forecast_id": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
      "tenant_id": "tenant-abc",
      "site_id": "site-001",
      "interval_start": "2025-02-12T12:00:00",
      "predicted_covers": 85,
      "required_headcount": 4,
      "labor_cost_to_revenue_ratio": 0.22,
      "created_at": "2025-02-11T14:30:00.123456+00:00"
    }
  ]
}
 */
export const getLaborForecast = async (siteId, date, options = {}) => {
  try {
    const forecasts = await listForecasts(siteId, date);
    if (!forecasts || forecasts.length === 0) return { intervals: [] };

    const intervals = forecasts.map((f, i) => forecastToInterval(f, i));

    if (options.roleGroup && options.roleGroup !== 'All') {
      return { intervals: intervals.filter(inv => inv.role_group === options.roleGroup) };
    }
    return { intervals };
  } catch (error) {
    console.error('Error fetching labor forecast:', error);
    throw error;
  }
};

/**
 * Get recommended staffing for a site and date (from shift scheduling Lambda).
 * Used by heatmap and "Apply recommended staffing".
 * @param {string} siteId - site_id
 * @param {string} date - YYYY-MM-DD
 * @param {{ roleGroup?: string }} options
 * 
 * ⚠️ COMMENTED OUT - Endpoint not implemented in backend: GET /labor-forecast/recommendations
 */
// export const getStaffingRecommendations = async (siteId, date, options = {}) => {
//   try {
//     const token = await getAuthToken();
//     if (!token) throw new Error('Authentication token not available');

//     const params = new URLSearchParams({ site_id: siteId, date });
//     if (options.roleGroup) params.set('role_group', options.roleGroup);

//     const response = await fetch(`${API_BASE_URL}/labor-forecast/recommendations?${params}`, {
//       method: 'GET',
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//     });

//     if (!response.ok) {
//       if (response.status === 404) return { recommendations: [] };
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }

//     const data = await response.json();
//     return data;
//   } catch (error) {
//     console.error('Error fetching staffing recommendations:', error);
//     throw error;
//   }
// };

/**
 * Apply recommended staffing to shift roster (calls shift scheduling Lambda).
 * @param {string} siteId - site_id
 * @param {string} date - YYYY-MM-DD
 * @param {Object} recommendations - payload from getStaffingRecommendations or heatmap selection
 * 
 * ⚠️ COMMENTED OUT - Endpoint not implemented in backend: POST /labor-forecast/apply
 */
// export const applyRecommendedStaffing = async (siteId, date, recommendations) => {
//   try {
//     const token = await getAuthToken();
//     if (!token) throw new Error('Authentication token not available');

//     const response = await fetch(`${API_BASE_URL}/labor-forecast/apply`, {
//       method: 'POST',
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ site_id: siteId, date, recommendations }),
//     });

//     if (!response.ok) {
//       const err = await response.json().catch(() => ({}));
//       throw new Error(err.message || `HTTP error! status: ${response.status}`);
//     }

//     return response.json();
//   } catch (error) {
//     console.error('Error applying recommended staffing:', error);
//     throw error;
//   }
// };

/**
 * Export staffing plan (CSV or Excel).
 * @param {string} siteId - site_id
 * @param {string} date - YYYY-MM-DD
 * @param {'csv'|'xlsx'} format
 * 
 * ⚠️ COMMENTED OUT - Endpoint not implemented in backend: GET /labor-forecast/export
 */
// export const exportStaffingPlan = async (siteId, date, format = 'csv') => {
//   try {
//     const token = await getAuthToken();
//     if (!token) throw new Error('Authentication token not available');

//     const response = await fetch(
//       `${API_BASE_URL}/labor-forecast/export?site_id=${encodeURIComponent(siteId)}&date=${date}&format=${format}`,
//       {
//         method: 'GET',
//         headers: { Authorization: `Bearer ${token}` },
//       }
//     );

//     if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

//     const blob = await response.blob();
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `staffing-plan-${siteId}-${date}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
//     a.click();
//     URL.revokeObjectURL(url);
//     return { success: true };
//   } catch (error) {
//     console.error('Error exporting staffing plan:', error);
//     throw error;
//   }
// };
